/** Opaque severity ID as stored in sc_severities, e.g. 's0'. Use SeverityDto.label for display. */
export type ScorecardSeverity = string;

export interface SeverityDto {
  /** Opaque DB ID, e.g. 's0'. Use label for display. */
  id: string;
  /** Order rank: lower = more severe. */
  rank: number;
  /** Human-readable label from sc_severities, e.g. 'Critical'. */
  label: string;
}

export const CHECK_RESULTS = ['passed', 'failed'] as const;
export type CheckResult = (typeof CHECK_RESULTS)[number];

export interface ScorecardDto {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  lastSynced?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScorecardCheckDto {
  id: string;
  version: number;
  name: string;
  description?: string;
  severity: ScorecardSeverity;
  category: string;
  scorecardId?: string;
  purpose?: string;
  active: boolean;
  lastSynced?: string;
  createdAt: string;
  updatedAt: string;
  checkJson?: Record<string, unknown>;
}

export interface ScorecardResultDto {
  snapshotTime: string;
  entityRef: string;
  team?: string;
  checkId: string;
  scorecardId: string;
  result: CheckResult;
  severity?: ScorecardSeverity;
  factValue?: unknown;
  checkVersion: number;
}

export interface SnapshotDto {
  snapshotTime: string;
  entityRef: string;
  team?: string;
  checkId: string;
  scorecardId: string;
  result: CheckResult;
  severity?: ScorecardSeverity;
  factValue?: unknown;
  checkVersion: number;
}

export interface CategoryDto {
  id: string;
  name: string;
  description?: string;
  /** Score (0–100) the category is expected to reach. */
  target: number;
  docsUrl?: string;
}

export const UNCATEGORIZED_CATEGORY_NAME = 'Uncategorized';

export type CategoryStatus = 'on-target' | 'below-target';

export const CATEGORY_STATUS = {
  ON_TARGET: 'on-target',
  BELOW_TARGET: 'below-target',
} as const satisfies Record<string, CategoryStatus>;

export interface TeamCategorySummaryDto {
  /** null for checks without a category ("Uncategorized"). */
  id: string | null;
  /**
   * sc_categories name, or the raw check category when no category definition
   * exists, or UNCATEGORIZED_CATEGORY_NAME.
   */
  name: string;
  description?: string;
  docsUrl?: string;
  target?: number;
  /** round(100 × passed / (passed + failed)); null when nothing applies. */
  score: number | null;
  /** null when the category has no target or no score. */
  status: CategoryStatus | null;
  /** Projects with at least one snapshot for a check in this category. */
  inScope: number;
  /**
   * Projects counted once, under their most severe failing check in this
   * category. Passing projects = inScope − sum of these.
   */
  failingBySeverity: Record<string, number>;
}

export interface TeamScorecardSummaryDto {
  categories: TeamCategorySummaryDto[];
  projects: TeamProjectDto[];
}

export interface TeamFailingCheckDto {
  checkId: string;
  name: string;
  severity: ScorecardSeverity;
  category: string | null;
}

export interface TeamProjectDto {
  projectPath: string;
  projectName: string;
  entityRef: string;
  /** Catalog component whose gitlab.com/project-slug matches projectPath. */
  catalogEntityRef?: string;
  /** spec.type of that component. */
  kind?: string;
  /** spec.lifecycle of that component. */
  lifecycle?: string;
  passed: number;
  /** Applicable checks: those with a snapshot for this project. */
  total: number;
  lastCheckedAt: string;
  stale: boolean;
  /** Ordered Critical → Low, then by name. */
  failingChecks: TeamFailingCheckDto[];
}
